import { api, apiBlob } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type { SurveyPlan, SurveyPlanDetail } from '../survey/surveyTypes';
import type { DevelopmentPlan, DevelopmentPlanDetail } from './developmentPlanTypes';

/** What the register may be narrowed by. */
export interface DevelopmentPlanListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** The RESPONSE's own live-or-deleted state — the shell's key. */
  status?: string;
  /** Plan-status facet values. Named `planStatus` because `status` is the shell's. */
  planStatus?: string;
}

function toQueryString(query: DevelopmentPlanListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Sent whenever PRESENT, empty string included — "none of these" matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.planStatus !== undefined) params.set('planStatus', query.planStatus);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Learning & Development › Development Plan — read only, on `development-plans.read`. Writing and
 * submitting a plan are Survey's own calls (`surveyRespondentsApi`), on Survey's keys.
 */
export const developmentPlansApi = {
  /**
   * The register's rows — every survey's PLANS, each with the employees it was created for. The
   * same rows Survey › Development Plan lists, on this module's read.
   */
  listPlans: (query: DevelopmentPlanListQuery, signal?: AbortSignal) =>
    api<PagedResult<SurveyPlan>>(`/api/development-plans/plans${toQueryString(query)}`, { signal }),

  /** One plan as created, and its employees — whichever survey it is under. */
  getPlan: (planId: string, signal?: AbortSignal) =>
    api<SurveyPlanDetail>(`/api/development-plans/plans/${planId}`, { signal }),

  list: (query: DevelopmentPlanListQuery, signal?: AbortSignal) =>
    api<PagedResult<DevelopmentPlan>>(`/api/development-plans${toQueryString(query)}`, { signal }),

  /** One plan with its needs. 403 for an employee above the reader's access level. */
  get: (id: string, signal?: AbortSignal) => api<DevelopmentPlanDetail>(`/api/development-plans/${id}`, { signal }),

  /**
   * Fetches ONE file attached to the employee's training report — `development-plans.download`. A
   * FETCH then an object URL, never an `<a href>` at the endpoint: it carries a bearer token no link
   * sends, and is served as an attachment.
   */
  downloadTrainingReportAttachment: (id: string, attachmentId: string) =>
    apiBlob(`/api/development-plans/${id}/training-report/attachments/${attachmentId}`),
};
